import React, { useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import {
  usePostRegisterMutation,
  useValidateInviteQuery,
  useGetMembershipInvitationQuery,
  usePostRegisterViaInviteMutation,
} from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/authService";
import { setDeviceToken } from "@/util/device";
import {
  getEmailValidationPattern,
  hookFormValidatePassword,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS_HINT,
} from "@/util/validation";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OAuthLoginButtons from "@/components/OAuthLoginButtons";
import RegisterData from "../model/interface/RegisterData";

export default function RegisterForm() {
  const navigate = useNavigate();
  const { refreshAuthStatus } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  // Org-membership invite (joining an existing org) — a separate flow from the registration invite.
  const memberInviteToken = searchParams.get("memberInvite");
  const isMemberInvite = !!memberInviteToken;

  const inviteResult = useValidateInviteQuery(inviteToken ?? "", { skip: !inviteToken });
  const memberInviteResult = useGetMembershipInvitationQuery(memberInviteToken ?? "", {
    skip: !memberInviteToken,
  });

  const [postRegister, postRegisterResult] = usePostRegisterMutation();
  const [registerViaInvite, registerViaInviteResult] = usePostRegisterViaInviteMutation();
  // Whichever submission path is active drives the loading/error/success UI.
  const submitResult = isMemberInvite ? registerViaInviteResult : postRegisterResult;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: FieldValues) => {
    if (isMemberInvite) {
      registerViaInvite({ ...data, token: memberInviteToken! } as RegisterData & { token: string });
    } else {
      postRegister({ ...data, invite: inviteToken ?? undefined } as RegisterData);
    }
  };

  useEffect(() => {
    if (inviteResult.data) {
      setValue("email", inviteResult.data.email);
      if (inviteResult.data.firstName) setValue("firstName", inviteResult.data.firstName);
      if (inviteResult.data.lastName) setValue("lastName", inviteResult.data.lastName);
      if (inviteResult.data.company) setValue("businessName", inviteResult.data.company);
    }
  }, [inviteResult.data, setValue]);

  useEffect(() => {
    if (memberInviteResult.data) {
      setValue("email", memberInviteResult.data.email);
      if (memberInviteResult.data.firstName) setValue("firstName", memberInviteResult.data.firstName);
      if (memberInviteResult.data.lastName) setValue("lastName", memberInviteResult.data.lastName);
    }
  }, [memberInviteResult.data, setValue]);

  useEffect(() => {
    if (submitResult.isSuccess) {
      const { accessToken, refreshToken, deviceToken } = submitResult.data;
      authService.setAccessToken(accessToken);
      authService.setRefreshToken(refreshToken);
      if (deviceToken) {
        setDeviceToken(deviceToken);
      }
      refreshAuthStatus().then(() => navigate("/dashboard"));
    }
  }, [submitResult, navigate, refreshAuthStatus]);

  // Show the invite-only wall only when neither a registration nor a membership invite is valid.
  const inviteInvalid =
    (!inviteToken && !memberInviteToken) ||
    (!!inviteToken && inviteResult.isError) ||
    (!!memberInviteToken && memberInviteResult.isError);

  if (inviteInvalid) {
    return (
      <div className="w-full max-w-md space-y-6 text-foreground">
        <div className="flex justify-center">
          <Link to="/">
            <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
          </Link>
        </div>
        <div className="rounded-xl border bg-card shadow-sm p-8 space-y-4 text-center">
          <div className="flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-6 w-6 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Registration is invite-only</h1>
          <p className="text-sm text-muted-foreground">
            {memberInviteToken
              ? apiErrorMessage(
                  memberInviteResult.error,
                  "This invitation is no longer valid. It may have been used already, replaced by a newer invitation or cancelled.",
                )
              : !inviteToken
                ? "You need an invite link to create a Retreevo account. Request access and we'll email you one."
                : apiErrorMessage(
                    inviteResult.error,
                    "This invite link is not valid. Request access and we'll email you a new one.",
                  )}
          </p>
          <Button asChild className="w-full">
            <Link to="/request-access">Request access</Link>
          </Button>
          <div className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-foreground hover:text-primary">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (inviteResult.isLoading || memberInviteResult.isLoading) {
    return (
      <div className="w-full max-w-md flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="flex justify-center">
        <Link to="/">
          <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
        </Link>
      </div>

      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {isMemberInvite
              ? `Join ${memberInviteResult.data?.organisationName ?? "the team"}`
              : "Create your workspace"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isMemberInvite
              ? "Set a password to accept your invitation and join the team."
              : "Your invite was approved. Set up Retreevo in under 10 minutes."}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                type="text"
                autoComplete="given-name"
                required
                {...register("firstName", { required: true, minLength: 2, maxLength: 50 })}
              />
              {errors.firstName && (
                <p className="text-xs text-destructive">First name is required</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                type="text"
                autoComplete="family-name"
                required
                {...register("lastName", { required: true, minLength: 2, maxLength: 50 })}
              />
              {errors.lastName && (
                <p className="text-xs text-destructive">Last name is required</p>
              )}
            </div>
          </div>

          {!isMemberInvite && (
            <div className="space-y-1.5">
              <Label htmlFor="businessName">Business name</Label>
              <Input
                id="businessName"
                type="text"
                placeholder="Acme Real Estate"
                autoComplete="organization"
                required
                {...register("businessName", { required: true, minLength: 2, maxLength: 100 })}
              />
              {errors.businessName && (
                <p className="text-xs text-destructive">Business name is required</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              readOnly
              className="bg-muted"
              {...register("email", { required: true, pattern: getEmailValidationPattern() })}
            />
            <p className="text-xs text-muted-foreground">
              Your invite is tied to this email address.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              {...register("password", {
                required: true,
                validate: hookFormValidatePassword({ minChars: PASSWORD_MIN_LENGTH }),
              })}
            />
            {errors.password ? (
              <p className="text-xs text-destructive">
                {(errors.password.message as string) || PASSWORD_REQUIREMENTS_HINT}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_HINT}</p>
            )}
          </div>

          {submitResult.isError && (
            <p className="text-sm text-destructive">
              {apiErrorMessage(submitResult.error, "Registration failed. Please try again.")}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={submitResult.isLoading}>
            {submitResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isMemberInvite ? "Join team" : "Create workspace"}
          </Button>
        </form>

        <OAuthLoginButtons
          invite={inviteToken ?? undefined}
          memberInvite={memberInviteToken ?? undefined}
        />

        <div className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-foreground hover:text-primary">
            Sign in
          </Link>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        By creating an account, you agree to our{" "}
        <a href="/terms-of-use" className="underline hover:text-foreground">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/privacy-policy" className="underline hover:text-foreground">
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}
